import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, ChevronRight } from 'lucide-react';
import { normalizeType, hasVal, fmtWon, fmtPct, pctColor, glassCard } from './etfUtils';

function MonthlyDividendETFs() {
  const [etfs, setEtfs] = useState([]);
  const [activeTab, setActiveTab] = useState('korea'); // 'korea' or 'usa'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/products`)
      .then(res => res.json())
      .then(data => {
        const monthlyETFs = (Array.isArray(data) ? data : []).filter(p => p.isMonthly);
        setEtfs(monthlyETFs);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // 구분 셀렉트 값(type)을 정규화해 탭과 매칭
  const filteredETFs = etfs.filter(etf => normalizeType(etf.type) === activeTab);
  const krCount = etfs.filter(e => normalizeType(e.type) === 'korea').length;
  const usCount = etfs.filter(e => normalizeType(e.type) === 'usa').length;

  const TabButton = ({ tab, count, children }) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`font-bold px-6 py-2.5 rounded-full text-sm transition-all ` +
        (activeTab === tab ? 'text-white shadow-md' : 'text-gray-400 hover:bg-white/10')}
      style={activeTab === tab ? { background: 'rgba(59, 130, 246, 0.8)' } : {}}
    >
      {children} <span className="opacity-70">{count}</span>
    </button>
  );

  if (loading) {
    return <div className="text-center p-8 text-gray-400">Loading ETFs...</div>;
  }

  return (
    <div className="rounded-3xl p-7 md:p-8 mt-4" style={glassCard}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <h3 className="text-2xl font-black">ISA/IRP 맞춤형 월배당 ETF Top Pick</h3>
        <Link to="/compare"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 border border-white/15 hover:bg-white/20 text-sm font-bold no-underline text-white transition-colors">
          <BarChart3 className="w-4 h-4" /> ETF 비교하기
        </Link>
      </div>

      {/* 탭 버튼 */}
      <div className="flex items-center gap-2 mb-6 p-2 rounded-full w-fit" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <TabButton tab="korea" count={krCount}>🇰🇷 한국형 ETF</TabButton>
        <TabButton tab="usa" count={usCount}>🇺🇸 미국형 ETF</TabButton>
      </div>

      {/* ETF 리스트 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredETFs.length > 0 ? (
          filteredETFs.map(etf => {
            const r = etf.returns || {};
            const price = hasVal(etf.price) ? fmtWon(etf.price) : null;
            const y1 = hasVal(r['1y']) ? fmtPct(r['1y']) : null;
            const m1 = hasVal(r['1m']) ? fmtPct(r['1m']) : null;
            return (
              <Link
                key={etf.id}
                to={`/etf/${encodeURIComponent(etf.id)}`}
                className="group block rounded-2xl p-5 no-underline border border-transparent hover:border-white/15 transition-colors"
                style={{ background: 'rgba(0,0,0,0.25)' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-bold text-base text-white leading-snug">{etf.name}</p>
                    {hasVal(etf.ticker) && <p className="text-sm text-gray-400 font-mono">{etf.ticker}</p>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/25 group-hover:text-white/60 flex-shrink-0 mt-1 transition-colors" />
                </div>

                {/* 시세·수익률 */}
                {(price || y1 || m1) && (
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-3">
                    {price && (
                      <span className="text-white font-bold text-lg tabular-nums">{price}</span>
                    )}
                    {y1 && (
                      <span className="text-sm font-semibold tabular-nums" style={{ color: pctColor(r['1y']) }}>
                        1년 {y1}
                      </span>
                    )}
                    {m1 && (
                      <span className="text-xs font-medium tabular-nums" style={{ color: pctColor(r['1m']) }}>
                        1개월 {m1}
                      </span>
                    )}
                  </div>
                )}

                {/* 태그 */}
                {(etf.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {etf.tags.map(tag => (
                      <span key={tag} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })
        ) : (
          <p className="text-gray-500 col-span-full text-center py-4">해당 유형의 월배당 ETF 정보가 없습니다.</p>
        )}
      </div>
    </div>
  );
}

export default MonthlyDividendETFs;
